import "./scrollbar.css";

import { Box, Flex, useDisclosure } from "@chakra-ui/react";
import { motion } from "framer-motion";
import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
} from "react";

import useIsOverflowing from "../../hooks/useIsOverflowing";
import { useIsSmallScreen } from "../../hooks/useIsSmallScreen";
import { SpotlightSearchState } from "../SpotlightSearch";
import AnimatedLogo from "./AnimatedLogo";
import { COLLAPSED_WIDTH, sidebarAnimation } from "./animations";
import MobileHeader from "./MobileHeader";
import SidebarNavItem, { NavItem } from "./SidebarNavItem";
import useSidebarState from "./useSidebarState";

type SidebarNavBaseProps = {
  app: "main" | "plan";
  navItems: NavItem[];
  footerNavItems: NavItem[];
  skipAnimationDelay?: boolean;
  searchState?: SpotlightSearchState;
  onAnimationComplete?(): void;
  onShowSearch?(): void;
  isImpersonated?: boolean;
};

export const SidebarNavBase: React.FC<
  PropsWithChildren<SidebarNavBaseProps>
> = ({
  app,
  navItems,
  footerNavItems,
  skipAnimationDelay,
  searchState,
  onShowSearch,
  isImpersonated = false,
  onAnimationComplete,
  children,
}) => {
  const isSmallScreen = useIsSmallScreen();

  const { ref: navItemsContainerRef, isOverflowing: navItemsOverflowing } =
    useIsOverflowing<HTMLDivElement>();
  const hiddenGapRef = useRef(0);

  const assignNavItemsContainerRef = useCallback(
    (node: HTMLDivElement | null) => {
      navItemsContainerRef.current = node;

      if (!node) {
        hiddenGapRef.current = 0;
        return;
      }

      hiddenGapRef.current = Math.max(
        node.scrollHeight - node.clientHeight - node.scrollTop,
        0
      );
    },
    [navItemsContainerRef]
  );

  const onNavItemsScroll = (event: React.UIEvent<HTMLDivElement>): void => {
    const target = event.currentTarget;
    const hiddenAmount =
      target.scrollHeight - target.clientHeight - target.scrollTop;
    hiddenGapRef.current = Math.max(hiddenAmount, 0);
  };

  const adjustScrollForHiddenContent = useCallback(() => {
    const container = navItemsContainerRef.current;
    if (!container) return;

    const hiddenAmount =
      container.scrollHeight - container.clientHeight - container.scrollTop;
    const normalizedHidden = Math.max(hiddenAmount, 0);
    const delta = normalizedHidden - hiddenGapRef.current;

    let nextHidden = normalizedHidden;
    if (delta > 0) {
      const adjustment = Math.min(normalizedHidden, delta * 2);
      container.scrollTop += adjustment;
      const recalculatedHidden =
        container.scrollHeight - container.clientHeight - container.scrollTop;
      nextHidden = Math.max(recalculatedHidden, 0);
    }

    hiddenGapRef.current = nextHidden;
  }, [navItemsContainerRef]);

  const [state, updateState] = useSidebarState();
  const { expandedItems } = state;

  const onExpand = (navItem: NavItem): void =>
    updateState({
      expandedItems: expandedItems.concat(navItem.label),
    });
  const onCollapse = (navItem: NavItem): void =>
    updateState({
      expandedItems: expandedItems.filter((label) => label !== navItem.label),
    });

  const { isOpen, onOpen, onClose } = useDisclosure({
    defaultIsOpen: state.isOpen,
  });

  useEffect(() => {
    if (!isOpen) {
      updateState({ isOpen: false, expandedItems: [] });
    } else {
      updateState({ isOpen: true });
    }
  }, [isOpen]);

  useEffect(() => {
    updateState({ skipAnimationDelay });
  }, [skipAnimationDelay]);

  if (isSmallScreen) {
    return (
      <MobileHeader
        app={app}
        onShowSearch={onShowSearch}
        searchState={searchState}
        navItems={navItems}
        bottomNavItems={footerNavItems.slice(0, -1)}
        footerNavItems={footerNavItems.slice(-1)}
        onClose={() => updateState({ expandedItems: [] })}
        isImpersonated={isImpersonated}
      >
        {children}
      </MobileHeader>
    );
  }

  return (
    <>
      {/* 
        Flex column layout ensures proper height distribution when
        CallInProgressBanner is present - banner takes its height,
        page content fills remaining space via flex: 1 
      */}
      <Box
        ml={`${COLLAPSED_WIDTH}px`}
        display="flex"
        flexDirection="column"
        height="100vh"
      >
        {children}
      </Box>

      <Box
        position="fixed"
        top="0"
        bottom="0"
        left="0"
        zIndex="5"
        onMouseEnter={onOpen}
        onMouseLeave={onClose}
        bg={isImpersonated ? "navBackgroundImpersonated" : "navBackground"}
      >
        <motion.div
          {...sidebarAnimation}
          style={{ height: "100%" }}
          custom={{ skipAnimationDelay }}
          onAnimationComplete={onAnimationComplete}
          animate={isOpen ? "expanded" : "collapsed"}
        >
          <Flex direction="column" py="4" color="white" h="100%">
            <AnimatedLogo
              app={app}
              isOpen={isOpen}
              skipAnimationDelay={skipAnimationDelay}
            />

            <Box
              id="sidebar-nav-items"
              overflowY="auto"
              overflowX="hidden"
              mt="4"
              onScroll={onNavItemsScroll}
              ref={assignNavItemsContainerRef}
            >
              {navItems.map((navItem) => (
                <SidebarNavItem
                  sidebarIsOpen={isOpen}
                  skipAnimationDelay={skipAnimationDelay}
                  key={navItem.label}
                  navItem={navItem}
                  isExpanded={expandedItems.includes(navItem.label)}
                  onExpand={() => onExpand?.(navItem)}
                  onCollapse={() => onCollapse?.(navItem)}
                  onChangeHeight={adjustScrollForHiddenContent}
                />
              ))}
            </Box>

            <Box
              mt="auto"
              flexShrink="0"
              borderTop="1px solid"
              borderColor={
                navItemsOverflowing ? "whiteAlpha.300" : "transparent"
              }
            >
              {footerNavItems.map((navItem) => (
                <SidebarNavItem
                  sidebarIsOpen={isOpen}
                  skipAnimationDelay={skipAnimationDelay}
                  key={navItem.label}
                  navItem={navItem}
                />
              ))}
            </Box>
          </Flex>
        </motion.div>
      </Box>
    </>
  );
};
